import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import EmailEditor, { EditorRef } from 'react-email-editor';

import {
  useCreateEmailByAdminMutation,
  useUpdateEmailByAdminMutation,
} from '@/apis/extended/admin/emailApi';
import { CommonDialog } from '@/components/Common/CommonDialog';
import { CommonFormControl } from '@/components/Common/CommonFormControl';
import { Email } from '@/types/EmailType';
import { DefaultEmailTemplate } from '../Admin/ManageEmail/DefaultEmailTemplate';

const initialForm = {
  key: '',
  title: '',
  category: 'system' as
    | 'contract'
    | 'interest_rate'
    | 'system'
    | 'marketing'
    | 'shopping',
  eventType: '',
  contractStage: '',
  targetRoles: [] as string[],
  deepLinkTemplate: '',
  description: '',
  isActive: true,
};

const contractStages = [
  'draft',
  'negotiating',
  'awaiting_signatures',
  'ratified',
  'inspection_pending',
  'repair_requested',
  'repair_negotiating',
  'escrow',
  'completed',
];

const roles = ['buyer', 'seller', 'agent', 'admin'];

const BASE_EMAIL_HEADER_HTML = `
<!-- base-email-header -->
<div style="background:#f1f3f5;padding:24px 16px;text-align:center;">
  <a href="{{siteUrl}}" style="display:inline-block;">
    <img src="https://assets.unlayer.com/projects/0/1720369596977-Asset%202-8.png" alt="Unbeatable" style="max-width:220px;height:auto;border:0;"/>
  </a>
</div>
`;

const BASE_EMAIL_FOOTER_HTML = `
<!-- base-email-footer -->
<div style="background:#f1f3f5;padding:24px 16px;text-align:center;color:#6b7280;font-size:12px;">
  <div>Unbeatable • ContractFlow</div>
  <div style="margin-top:6px;">If you have questions, contact us at {{supportEmail}}</div>
</div>
`;

type AddEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  email?: Partial<Email>;
};

export const AddEmailModal = ({
  isOpen,
  onClose,
  email,
}: AddEmailModalProps) => {
  const [createEmail, { isLoading: isCreatingEmail }] =
    useCreateEmailByAdminMutation();
  const [updateEmail, { isLoading: isUpdatingEmail }] =
    useUpdateEmailByAdminMutation();
  const isLoading = isCreatingEmail || isUpdatingEmail;

  const [form, setForm] = useState({ ...initialForm });
  const editorRef = useRef<EditorRef>(null);

  useEffect(() => {
    if (!email) {
      setForm({ ...initialForm });
      return;
    }
    setForm({
      key: email.key || '',
      title: email.title || '',
      category: email.category || 'system',
      eventType: email.eventType || '',
      contractStage: email.contractStage || '',
      targetRoles: email.targetRoles || [],
      deepLinkTemplate: email.deepLinkTemplate || '',
      description: email.description || '',
      isActive: email.isActive !== false,
    });
  }, [email]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setForm({
      ...form,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleSelectChange = (name: string, value: any) => {
    setForm({ ...form, [name]: value });
  };

  const handleRoleToggle = (role: string) => {
    const currentRoles = form.targetRoles || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter((r) => r !== role)
      : [...currentRoles, role];
    setForm({ ...form, targetRoles: newRoles });
  };

  const handleOnReady = () => {
    let design = DefaultEmailTemplate;
    try {
      if (email?.design) design = JSON.parse(email.design);
    } catch (ex) {}
    if (!editorRef?.current?.editor) return;

    editorRef.current.editor.loadDesign(design);
    // let mergeTags = tags;
    // editorRef.current.editor.setMergeTags(mergeTags);
  };

  const handleAddEmail = () => {
    if (!form.key || !form.title) return;
    if (!editorRef?.current?.editor) return;

    editorRef.current.editor.exportHtml((data) => {
      const ensureBaseElements = (html: string) => {
        const hasHeader = html.includes('base-email-header');
        const hasFooter = html.includes('base-email-footer');
        let next = html;
        if (!hasHeader) {
          next = next.replace(
            /<body[^>]*>/i,
            (match) => `${match}${BASE_EMAIL_HEADER_HTML}`
          );
        }
        if (!hasFooter) {
          next = next.replace(/<\/body>/i, `${BASE_EMAIL_FOOTER_HTML}</body>`);
        }
        if (!/<body[^>]*>/i.test(next)) {
          next = `${BASE_EMAIL_HEADER_HTML}${next}${BASE_EMAIL_FOOTER_HTML}`;
        }
        return next;
      };

      const emailData = {
        ...form,
        design: JSON.stringify(data.design),
        content: ensureBaseElements(data.html),
      };

      if (email?._id) {
        updateEmail({
          ...emailData,
          id: email._id,
        })
          .unwrap()
          .then(() => setForm({ ...initialForm }));
      } else {
        createEmail(emailData)
          .unwrap()
          .then(() => setForm({ ...initialForm }));
      }
      onClose();
    });
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      title={email?._id ? 'Edit Email' : 'Add Email'}
      icon="fa-light fa-hexagon-plus"
      confirmButtonText={email?._id ? 'Edit Email' : 'Add Email'}
      closeButtonText="Close"
      isLoading={isLoading}
      onConfirm={handleAddEmail}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '100%',
        },
      }}
    >
      <Box>
        <CommonFormControl
          label="Key:"
          sx={{ mt: 1 }}
          error={form.key ? '' : 'This field is required!'}
        >
          <TextField
            placeholder="Key"
            name="key"
            fullWidth
            value={form.key}
            onChange={handleInputChange}
          />
        </CommonFormControl>
        <CommonFormControl
          label="Title:"
          sx={{ mt: 1 }}
          error={form.title ? '' : 'This field is required!'}
        >
          <TextField
            placeholder="Title"
            name="title"
            fullWidth
            value={form.title}
            onChange={handleInputChange}
          />
        </CommonFormControl>
        <CommonFormControl label="Category:" sx={{ mt: 1 }}>
          <Select
            value={form.category}
            onChange={(e) => handleSelectChange('category', e.target.value)}
            fullWidth
          >
            <MenuItem value="contract">Contract</MenuItem>
            <MenuItem value="interest_rate">Interest Rate</MenuItem>
            <MenuItem value="system">System</MenuItem>
            <MenuItem value="marketing">Marketing</MenuItem>
            <MenuItem value="shopping">Shopping</MenuItem>
          </Select>
        </CommonFormControl>
        <CommonFormControl label="Event Type:" sx={{ mt: 1 }}>
          <TextField
            placeholder="e.g., contract_submitted, proposal_created"
            name="eventType"
            fullWidth
            value={form.eventType}
            onChange={handleInputChange}
            helperText="Unique identifier for this email event"
          />
        </CommonFormControl>
        {form.category === 'contract' && (
          <CommonFormControl label="Contract Stage:" sx={{ mt: 1 }}>
            <Select
              value={form.contractStage}
              onChange={(e) =>
                handleSelectChange('contractStage', e.target.value)
              }
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {contractStages.map((stage) => (
                <MenuItem key={stage} value={stage}>
                  {stage
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
          </CommonFormControl>
        )}
        <CommonFormControl label="Target Roles:" sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {roles.map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    checked={form.targetRoles?.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                  />
                }
                label={role}
              />
            ))}
          </Box>
        </CommonFormControl>
        <CommonFormControl label="Deep Link Template:" sx={{ mt: 1 }}>
          <TextField
            placeholder="/buyer/property-dashboard/{{contractId}}"
            name="deepLinkTemplate"
            fullWidth
            value={form.deepLinkTemplate}
            onChange={handleInputChange}
            helperText="Use {{variableName}} for dynamic values"
          />
        </CommonFormControl>
        <CommonFormControl label="Description:" sx={{ mt: 1 }}>
          <TextField
            placeholder="Admin description of when this email is sent"
            name="description"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={handleInputChange}
          />
        </CommonFormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.isActive}
              onChange={(e) => handleSelectChange('isActive', e.target.checked)}
            />
          }
          label="Active"
          sx={{ mt: 1 }}
        />
        <Box sx={{ mt: 3, borderRadius: '12px', overflow: 'hidden' }}>
          <EmailEditor
            style={{ height: '550px' }}
            ref={editorRef}
            onReady={handleOnReady}
          />
        </Box>
      </Box>
    </CommonDialog>
  );
};
